import type { AxiosResponse } from 'axios'
import { useMutation } from 'react-query'
import { requestProvider } from './requestProvider'

const baseUrl = () => 'admin/equipment-migration'

function isAxiosResponse<T>(value: unknown): value is AxiosResponse<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    'status' in value &&
    'headers' in value
  )
}

export interface IEquipmentMigrationRequest {
  sourceServiceStationId: number
  targetServiceStationId: number
}

export interface IMigrationEquipmentSummary {
  id: number
  name: string
  serialNumber: string
  status: string
}

export interface IEquipmentMigrationPreview {
  sourceServiceStationId: number
  sourceServiceStationName: string
  sourceCustomerName: string
  targetServiceStationId: number
  targetServiceStationName: string
  targetCustomerName: string
  equipmentToMigrateCount: number
  equipmentToMigrate: IMigrationEquipmentSummary[]
  impregnationRecordsToUpdate: number
  activeDeviationsToUpdate: number
}

export interface IEquipmentMigrationResult {
  migratedCount: number
  impregnationRecordsUpdated: number
  deviationsUpdated: number
}

/** Supports body-only or full Axios response, and camelCase or PascalCase JSON from the API. */
export function parseEquipmentMigrationPreviewResponse(mutationResult: unknown): IEquipmentMigrationPreview {
  const body = isAxiosResponse<unknown>(mutationResult) ? mutationResult.data : mutationResult
  const raw = body as Record<string, unknown>

  const equipmentRaw = (raw.equipmentToMigrate ?? raw.EquipmentToMigrate) as unknown[] | undefined
  const equipmentToMigrate: IMigrationEquipmentSummary[] = (equipmentRaw ?? []).map(eq => {
    const row = eq as Record<string, unknown>
    return {
      id: Number(row.id ?? row.Id),
      name: String(row.name ?? row.Name ?? ''),
      serialNumber: String(row.serialNumber ?? row.SerialNumber ?? ''),
      status: String(row.status ?? row.Status ?? ''),
    }
  })

  return {
    sourceServiceStationId: Number(raw.sourceServiceStationId ?? raw.SourceServiceStationId),
    sourceServiceStationName: String(raw.sourceServiceStationName ?? raw.SourceServiceStationName ?? ''),
    sourceCustomerName: String(raw.sourceCustomerName ?? raw.SourceCustomerName ?? ''),
    targetServiceStationId: Number(raw.targetServiceStationId ?? raw.TargetServiceStationId),
    targetServiceStationName: String(raw.targetServiceStationName ?? raw.TargetServiceStationName ?? ''),
    targetCustomerName: String(raw.targetCustomerName ?? raw.TargetCustomerName ?? ''),
    equipmentToMigrateCount: Number(raw.equipmentToMigrateCount ?? raw.EquipmentToMigrateCount ?? 0),
    equipmentToMigrate,
    impregnationRecordsToUpdate: Number(raw.impregnationRecordsToUpdate ?? raw.ImpregnationRecordsToUpdate ?? 0),
    activeDeviationsToUpdate: Number(raw.activeDeviationsToUpdate ?? raw.ActiveDeviationsToUpdate ?? 0),
  }
}

export function parseEquipmentMigrationResultResponse(mutationResult: unknown): IEquipmentMigrationResult {
  const body = isAxiosResponse<unknown>(mutationResult) ? mutationResult.data : mutationResult
  const raw = body as Record<string, unknown>

  return {
    migratedCount: Number(raw.migratedCount ?? raw.MigratedCount ?? 0),
    impregnationRecordsUpdated: Number(raw.impregnationRecordsUpdated ?? raw.ImpregnationRecordsUpdated ?? 0),
    deviationsUpdated: Number(raw.deviationsUpdated ?? raw.DeviationsUpdated ?? 0),
  }
}

export const usePreviewEquipmentMigration = () =>
  useMutation((data: IEquipmentMigrationRequest) =>
    requestProvider.post<IEquipmentMigrationPreview>(`${baseUrl()}/preview`, data),
  )

export const useExecuteEquipmentMigration = () =>
  useMutation((data: IEquipmentMigrationRequest) =>
    requestProvider.post<IEquipmentMigrationResult>(`${baseUrl()}/execute`, data),
  )
